import { useCallback, useMemo } from "react";

import { usesMxEnrichment } from "byzantine/src/TransactionEnrichmentSource";

import { useAccountsContext } from "../../../../components/contexts/AccountContext";
import { useCurrentUser } from "../../../../components/contexts/CurrentUserContext";

import { useTransactionCategories } from "./useTransactionCategories";

import type {
  TransactionCategory,
  UseTransactionCategoriesResult,
} from "./useTransactionCategories";

export interface UseLiveLedgerCategoriesResult {
  consumerCategories: TransactionCategory[];
  businessCategories: TransactionCategory[];
  /** The taxonomy matching the logged-in user's own type, for a flat (non-mixed) filter list. */
  ownAccountType: "consumer" | "business";
  /** True when the accounts the user can see span both taxonomies. */
  isMixedAccountAccess: boolean;
  /** Union of both trees' top-level slugs when mixed, otherwise just the caller's own. */
  validSlugs: string[];
  /** Picks the tree matching the transaction's own account, not the viewer's. */
  categoriesForTransaction: (
    transaction: API.Transaction
  ) => TransactionCategory[];
  labelForSlug: (slug: string) => string;
  /** False for a selection that no longer matches an option, e.g. after the FI changes sources. */
  isAvailableSlug: (slug: string) => boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Live Ledger's category taxonomy is per-account, not per-viewer. Every account the user can
 * see is already tagged (via `enrichment_account_type`, FINT-575/576's precedent), so mixed
 * access is read straight off that list rather than a separate signal, and the second tree is
 * fetched only when it can actually be needed.
 */
export const useLiveLedgerCategories = (): UseLiveLedgerCategoriesResult => {
  const { accounts } = useAccountsContext();
  const { currentUser } = useCurrentUser();

  const accountTypesPresent = useMemo(() => {
    const types = new Set<"consumer" | "business">();
    (accounts ?? []).forEach((account) => {
      const type = account.enrichment_account_type;
      if (type === "consumer" || type === "business") types.add(type);
    });
    return types;
  }, [accounts]);

  const isMixedAccountAccess = accountTypesPresent.size > 1;

  // account_type_for_account keys off an account's own members, not who is asking, so a
  // consumer-profile user who is only a joint member on a shared business account still has
  // a single (business) type here, overriding the viewer's own profile below. Undefined when
  // there is no account signal to trust instead (accounts still loading, or none visible).
  const singleAccountType =
    accountTypesPresent.size === 1
      ? (Array.from(accountTypesPresent)[0] as "consumer" | "business")
      : undefined;

  const ownAccountType: "consumer" | "business" =
    singleAccountType ?? (currentUser?.isBusiness() ? "business" : "consumer");
  const otherAccountType: "consumer" | "business" =
    ownAccountType === "business" ? "consumer" : "business";

  // Explicit only when a single known account type overrides the viewer's own; otherwise the
  // bare endpoint already resolves server-side to the caller's own type, same as ownAccountType.
  const own = useTransactionCategories(
    singleAccountType ? { accountType: singleAccountType } : {}
  );
  const other = useTransactionCategories({
    accountType: otherAccountType,
    enabled: isMixedAccountAccess,
  });

  const consumerCategories =
    ownAccountType === "consumer" ? own.categories : other.categories;
  const businessCategories =
    ownAccountType === "business" ? own.categories : other.categories;

  const validSlugs = useMemo(
    () =>
      (isMixedAccountAccess
        ? [...consumerCategories, ...businessCategories]
        : own.categories
      ).map((category) => category.slug),
    [
      isMixedAccountAccess,
      consumerCategories,
      businessCategories,
      own.categories,
    ]
  );

  const consumerSource =
    ownAccountType === "consumer" ? own.source : other.source;
  const businessSource =
    ownAccountType === "business" ? own.source : other.source;

  const categoriesForTransaction = useCallback(
    (transaction: API.Transaction): TransactionCategory[] => {
      const isConsumer = isMixedAccountAccess
        ? (transaction.account_type ?? ownAccountType) === "consumer"
        : ownAccountType === "consumer";
      const tree: Pick<
        UseTransactionCategoriesResult,
        "categories" | "source"
      > = !isMixedAccountAccess
        ? own
        : {
            categories: isConsumer ? consumerCategories : businessCategories,
            source: isConsumer ? consumerSource : businessSource,
          };
      // A cached MX tree lists filter options only, so it must not drive the editor on a Narmi row.
      if (tree.source === "mx" && !usesMxEnrichment(transaction)) return [];
      return tree.categories;
    },
    [
      isMixedAccountAccess,
      own,
      ownAccountType,
      consumerCategories,
      businessCategories,
      consumerSource,
      businessSource,
    ]
  );

  const validSlugSet = useMemo(() => new Set(validSlugs), [validSlugs]);
  const isAvailableSlug = useCallback(
    (slug: string) => validSlugSet.has(slug),
    [validSlugSet]
  );

  const { labelForSlug: ownLabelForSlug } = own;
  const { labelForSlug: otherLabelForSlug } = other;
  const labelForSlug = useCallback(
    (slug: string) => {
      const ownLabel = ownLabelForSlug(slug);
      return ownLabel !== slug ? ownLabel : otherLabelForSlug(slug);
    },
    [ownLabelForSlug, otherLabelForSlug]
  );

  return {
    consumerCategories,
    businessCategories,
    ownAccountType,
    isMixedAccountAccess,
    validSlugs,
    categoriesForTransaction,
    labelForSlug,
    isAvailableSlug,
    isLoading: own.isLoading || (isMixedAccountAccess && other.isLoading),
    error: own.error ?? (isMixedAccountAccess ? other.error : null),
  };
};
